import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import queryString from 'query-string';
import styled from '@emotion/styled';
import { gridSize } from '@atlaskit/theme';
import Heading from '@atlaskit/heading';

import { applicationsMetadata } from '../utilities/applications';
import WhiteboxPage from '../components/WhiteboxPage';
import errorImage from '../images/Error.svg';
import { URLParameters, JWTValidationError } from '../types';
import messages from './SocialLoginRecoveryPage.messages';
import TallButton from '../components/TallButton';
import ButtonContainer from '../components/ButtonContainer';
import { isMobileOidc } from '../utilities/oidc/oidc';
import {
  useAnalyticsClient,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { useAppSelector } from '../stores/redux.store';
import { fetchPageState } from '../utilities/hooks/useGlobalPageState';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { checkFeatureGateOnceInitialized } from '../utilities/feature-flags/feature-gate';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';

export type SocialLoginRecoveryError =
  | JWTValidationError
  | 'invalid_source_flow'
  | 'no_recovery_method'
  | 'unknown_error';

const Image = styled.img`
  margin: ${gridSize()}px auto ${gridSize()}px;
  width: 100px;
`;

const Messsage = styled.div`
  margin-top: ${gridSize()}px;
  overflow-wrap: break-word;
`;

const ErrorMesssage = styled(Messsage)`
  text-align: center;
`;

const ExpectedEmailWrapper = styled.div`
  margin-top: ${gridSize() * 0.75}px;
  font-weight: bold;
`;

export const pageId = 'atlassianAccountSocialLoginRecoveryScreen';

export async function socialRecoveryLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/social/recovery/state', request, {
    capabilities: ['capability-group--login', 'capability-subgroup--social-login'],
  });
}

export const SocialLoginRecoveryPage = () => {
  const product = useMemo(() => {
    const queryParams = queryString.parse(window.location.search) as URLParameters;
    return queryParams.application && applicationsMetadata[queryParams.application];
  }, []);

  const { error, expectedEmail, foundEmail, recoveryUrl } = useAppSelector(
    state => state.socialLoginRecovery
  );

  const isMobileApp = useAppSelector(
    state => isMobileOidc(state.microbranding.oidcContext) || state.microbranding.isEmbedded
  );

  const { formatMessage } = useIntl();
  const analyticsClient = useAnalyticsClient();
  const navigate = useNavigateSoftOrHard();

  usePageViewedEvent(pageId, error && { errorCode: error });

  const recover = (recoveryUrl: URL) => {
    analyticsClient.buttonClickedEvent(pageId, 'tryAgainButton');
    navigate(recoveryUrl.toString());
  };

  const renderWhiteboxFooter = () => {
    return isMobileApp ? null : (
      <a
        id="social-login-recovery-documentation-link"
        target="_blank"
        rel="noreferrer noopener"
        onClick={() => analyticsClient.linkClickedEvent(pageId, 'documentationLink')}
        href="https://support.atlassian.com/atlassian-account/docs/login-issues-related-to-an-incorrect-email-address-error/">
        <FormattedMessage {...messages.documentationLinkMessage} />
      </a>
    );
  };

  const renderRecoveryPage = (recoveryUrl: URL, expectedEmail: string, foundEmail?: string) => {
    const destinationProduct = product ? product.name : formatMessage(messages.productFallback);

    return (
      <WhiteboxPage
        pageId={pageId}
        header={
          <Heading size="small" as="h1">
            <FormattedMessage {...messages.title} />
          </Heading>
        }
        whiteboxFooter={renderWhiteboxFooter()}>
        <Image src={errorImage} alt="" />
        <Messsage>
          {foundEmail ? (
            <FormattedMessage
              {...messages.bodyMessageUnableToLoginWithFoundEmail}
              values={{
                product: destinationProduct,
                foundEmail: foundEmail,
              }}
            />
          ) : (
            <FormattedMessage
              {...messages.bodyMessageUnableToLoginWithThisEmailAddress}
              values={{
                product: destinationProduct,
              }}
            />
          )}
        </Messsage>
        <Messsage>
          <FormattedMessage
            id="social.recovery.body.try.again"
            defaultMessage="To continue, select <b>Try again</b> and use"
            description={`Message instructing the user that they logged in via a social
          provider (eg. Log in with Google) and selected an account that we didn't expect.
          This occurs when we know that we want the user to log in with myemail@gmail.com, but
          in the social provider they log in with otheraccount@gmail.com. They should press the Try again button and use the expected email address.`}
            values={{ b: chunks => <strong>{chunks}</strong> }}
          />
          <ExpectedEmailWrapper>{expectedEmail}</ExpectedEmailWrapper>
        </Messsage>

        <ButtonContainer>
          <TallButton
            appearance="primary"
            id="recovery-button"
            isFullWidth
            onClick={() => recover(recoveryUrl)}>
            <FormattedMessage {...messages.buttonText} />
          </TallButton>
        </ButtonContainer>
      </WhiteboxPage>
    );
  };

  const renderRecoveryError = (error: SocialLoginRecoveryError) => {
    const errorMessage = () => {
      switch (error) {
        case 'expired_token':
          return messages.expiredTokenErrorMessage;
        case 'future_issued_token':
        case 'invalid_token':
          return messages.tokenErrorMessage;
        default:
          return messages.unknownErrorMessage;
      }
    };

    return (
      <WhiteboxPage
        pageId={pageId}
        header={
          <Heading size="small" as="h1">
            <FormattedMessage {...messages.errorTitle} />
          </Heading>
        }>
        <Image src={errorImage} alt="" />
        <ErrorMesssage data-testid="default-error-message">
          <FormattedMessage {...errorMessage()} />
        </ErrorMesssage>
      </WhiteboxPage>
    );
  };

  if (expectedEmail && recoveryUrl) {
    return renderRecoveryPage(recoveryUrl, expectedEmail, foundEmail);
  } else if (error) {
    return renderRecoveryError(error);
  } else {
    return renderRecoveryError('unknown_error');
  }
};

export default SocialLoginRecoveryPage;
